"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Maximize2, Minimize2 } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { useTenancy } from "@/contexts/TenancyContext";

import { getAllScopeItems } from "@/services/scopeService";
import { getAllLegalObligations } from "@/services/legalObligationsService";
import { getSpecialInterestGroups } from "@/services/specialInterestGroupsService";
import { getAllObjectives } from "@/services/objectivesService";
import { getAllControls } from "@/services/controlsService";
import { getAllRisks } from "@/services/risksService";
import { getAssets } from "@/services/assetsService";
import { getPolicyList } from "@/services/policyService";
import { getDocuments } from "@/services/documentsService";
import { getDataTypes } from "@/services/dataRetentionService";
import { getPrivacyRequestList } from "@/services/privacyRequestTracker";
import { getAllTasks } from "@/services/assuranceActionsService";
import { checklistService } from "@/app/pages/checklists/services/checklist-service";
import { AuditService } from "@/app/pages/auditcentre/auditService";

type GraphNode = {
  id: string;
  label: string;
  group: string;
  type: string;
  size?: number;
  details?: string;
};

type GraphLink = {
  source: string;
  target: string;
  value?: number;
};

type Metrics = Record<string, number>;

const MAX_ITEMS_PER_CATEGORY = 60;

const truncate = (value: unknown, max = 80) => {
  if (!value && value !== 0) return "Unknown";
  const str = String(value).trim();
  if (str.length <= max) return str;
  return `${str.slice(0, max - 1)}…`;
};

const slugify = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const ensureCompanyContext = (tenantId?: string) => {
  if (tenantId) {
    window.companyID = tenantId;
  }
};

const Duck: React.FC = () => {
  const { isDark } = useTheme();
  const { activeTenancy, isModuleEnabled } = useTenancy();

  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const graphCardRef = useRef<HTMLDivElement | null>(null);
  const forceGraphRef = useRef<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; links: GraphLink[] }>({ nodes: [], links: [] });
  const [metrics, setMetrics] = useState<Metrics>({});
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(async () => {
    if (!graphCardRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await graphCardRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error("Error toggling fullscreen:", err);
    }
  }, []);

  // Listen for fullscreen changes (e.g., user presses Esc)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Resize graph when entering/exiting fullscreen
  useEffect(() => {
    if (!forceGraphRef.current || !graphContainerRef.current) return;

    // Small delay to let the DOM update
    const timer = setTimeout(() => {
      if (!graphContainerRef.current) return;
      
      const newWidth = graphContainerRef.current.offsetWidth;
      const newHeight = graphContainerRef.current.offsetHeight;
      
      if (forceGraphRef.current && newWidth > 0 && newHeight > 0) {
        forceGraphRef.current.width(newWidth).height(newHeight);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isFullscreen]);

  const fetchDuckData = useCallback(async () => {
    const tenantId = activeTenancy?.id;
    if (!tenantId) {
      setGraphData({ nodes: [], links: [] });
      setMetrics({});
      return;
    }

    setLoading(true);
    setError(null);

    ensureCompanyContext(tenantId);

    try {
      const auditService = new AuditService({ activeTenancy: { id: tenantId } });

      const results = await Promise.allSettled([
        getAllScopeItems(tenantId),
        getAllLegalObligations(),
        getSpecialInterestGroups(tenantId),
        getAllObjectives(tenantId),
        getAllControls(isModuleEnabled),
        getAllRisks(tenantId),
        getAssets(),
        auditService.listStandaloneFindings(),
        auditService.listAudits(),
        getPolicyList(tenantId),
        getDocuments(tenantId),
        getDataTypes(),
        getPrivacyRequestList(tenantId),
        getAllTasks(tenantId),
        checklistService.getChecklists(tenantId),
      ]);

      const [
        scopeRes,
        obligationsRes,
        partiesRes,
        objectivesRes,
        controlsRes,
        risksRes,
        assetsRes,
        findingsRes,
        auditsRes,
        policiesRes,
        documentsRes,
        dataTypesRes,
        privacyRequestsRes,
        tasksRes,
        checklistsRes,
      ] = results;

      const scope = scopeRes.status === "fulfilled" ? scopeRes.value : null;
      const legalObligations = obligationsRes.status === "fulfilled" ? obligationsRes.value : [];
      const interestedParties = partiesRes.status === "fulfilled" ? partiesRes.value : [];
      const objectives = objectivesRes.status === "fulfilled" ? objectivesRes.value : [];
      const controls = controlsRes.status === "fulfilled" ? controlsRes.value : [];
      const risks = risksRes.status === "fulfilled" ? risksRes.value : [];
      const assets = assetsRes.status === "fulfilled" ? assetsRes.value : [];
      const findings = findingsRes.status === "fulfilled" ? findingsRes.value : [];
      const audits = auditsRes.status === "fulfilled" ? auditsRes.value : [];
      const policies = policiesRes.status === "fulfilled" ? policiesRes.value : [];
      const documents = documentsRes.status === "fulfilled" ? documentsRes.value : [];
      const dataTypes = dataTypesRes.status === "fulfilled" ? dataTypesRes.value : [];
      const privacyRequests = privacyRequestsRes.status === "fulfilled" ? privacyRequestsRes.value : [];
      const tasks = tasksRes.status === "fulfilled" ? tasksRes.value : [];
      const checklists = checklistsRes.status === "fulfilled" ? checklistsRes.value : [];

      const clauseItems = controls.filter((control) =>
        Boolean((control as any)?.metadata?.isClause || (control as any)?.metadata?.type === "clause")
      );
      const controlItems = controls.filter(
        (control) => !(control as any)?.metadata?.isClause && (control as any)?.metadata?.type !== "clause"
      );

      const suppliers = assets.filter((asset) => (asset as any)?.componentType === "Supplier");
      const plainAssets = assets.filter((asset) => (asset as any)?.componentType !== "Supplier");

      const storageLocations = Array.from(
        new Set(
          assets
            .flatMap((asset: any) => {
              const field =
                asset?.dataStorageLocations ??
                asset?.storageLocations ??
                asset?.storageLocation ??
                asset?.dataLocations ??
                [];
              if (Array.isArray(field)) return field;
              if (typeof field === "string") return field.split(/[,;]+/g);
              return [];
            })
            .map((entry) => (entry ? String(entry).trim() : ""))
            .filter(Boolean)
        )
      );

      const evidenceItems = documents.filter((doc: any) =>
        ["Evidence", "evidence", "Supporting Evidence"].includes(String(doc?.documentType ?? doc?.type ?? "")) ||
        String(doc?.status ?? "").toLowerCase() === "evidence"
      );

      const needsAndExpectations = interestedParties
        .map((party: any) => party?.comments || party?.requirementsAddressedThroughISMS)
        .filter(Boolean)
        .map((value, index) => ({ id: `need-${index}`, value }));

      const summaryMetrics: Metrics = {
        obligations: legalObligations.length,
        parties: interestedParties.length,
        needs: needsAndExpectations.length,
        objectives: objectives.length,
        clauses: clauseItems.length,
        controls: controlItems.length,
        risks: risks.length,
        suppliers: suppliers.length,
        assets: plainAssets.length,
        findings: findings.length,
        audits: audits.length,
        policies: policies.length,
        documents: documents.length,
        dataTypes: dataTypes.length,
        storageLocations: storageLocations.length,
        privacyRequests: privacyRequests.length,
        tasks: tasks.length,
        checklists: checklists.length,
        evidence: evidenceItems.length,
      };

      const nodes: GraphNode[] = [];
      const links: GraphLink[] = [];
      const nodeIdMap = new Map<string, string>();

      const rootId = "duck-core-scope";
      const scopeLabel = scope?.heading ? truncate(scope.heading, 60) : activeTenancy?.name ?? "Customer Scope";
      nodes.push({
        id: rootId,
        label: scopeLabel,
        group: "Core",
        type: "Scope",
        size: 40,
        details: truncate(
          Object.values(scope?.text ?? {}).join(" ") || "Central overview of the management system scope.",
          160
        ),
      });

      type CategoryConfig<T> = {
        id: string;
        label: string;
        group: string;
        type: string;
        dataset: T[];
        limit?: number;
        getId?: (item: T, index: number) => string | number | undefined;
        getLabel: (item: T, index: number) => string;
        getDetails?: (item: T, index: number) => string | undefined;
        linkValue?: number;
        nodeSize?: number;
      };

      const addCategory = <T,>(config: CategoryConfig<T>) => {
        nodes.push({
          id: config.id,
          label: config.label,
          group: config.group,
          type: config.type,
          size: 20,
        });
        links.push({ source: rootId, target: config.id, value: config.linkValue ?? 2 });

        const cap = config.limit ?? MAX_ITEMS_PER_CATEGORY;
        config.dataset.slice(0, cap).forEach((item, index) => {
          const rawId = config.getId ? config.getId(item, index) : (item as any)?.id ?? index;
          const nodeId = `${config.id}-${index}-${slugify(rawId ?? index)}`;
          nodes.push({
            id: nodeId,
            label: truncate(config.getLabel(item, index)),
            group: config.group,
            type: config.label,
            size: config.nodeSize ?? 6,
            details: config.getDetails ? truncate(config.getDetails(item, index), 120) : undefined,
          });
          links.push({ source: config.id, target: nodeId, value: 1 });
          
          // Map original item ID to graph node ID for cross-linking
          if (rawId) {
            const mapKey = `${config.id}:${rawId}`;
            nodeIdMap.set(mapKey, nodeId);
          }
        });
      };

      // Context categories - link directly to Scope
      addCategory({
        id: "duck-legal-obligations",
        label: "Legal Obligations",
        group: "Context",
        type: "Category",
        dataset: legalObligations,
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.requirement || item?.description || item?.id || "Legal Obligation",
        getDetails: (item: any) => `Owner: ${item?.owner || "Unassigned"}`,
        linkValue: 3,
      });

      addCategory({
        id: "duck-interested-parties",
        label: "Interested Parties",
        group: "Context",
        type: "Category",
        dataset: interestedParties,
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.groupName || item?.name || item?.primaryContact || "Interested Party",
        getDetails: (item: any) => item?.comments || item?.requirement || item?.typeOfRequest,
        linkValue: 3,
      });

      addCategory({
        id: "duck-needs",
        label: "Needs & Expectations",
        group: "Context",
        type: "Category",
        dataset: needsAndExpectations,
        getLabel: (item: any) => item?.value || "Need",
        getId: (item: any) => item?.id,
        linkValue: 3,
      });

      addCategory({
        id: "duck-objectives",
        label: "Objectives",
        group: "Planning",
        type: "Category",
        dataset: objectives,
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.objectiveText || "Objective",
        getDetails: (item: any) =>
          item?.objectiveTargetDate ? `Target: ${new Date(item.objectiveTargetDate).toLocaleDateString()}` : undefined,
        linkValue: 3,
      });

      // Governance hub - Controls and Clauses link to Scope
      addCategory({
        id: "duck-controls",
        label: "Controls",
        group: "Governance",
        type: "Category",
        dataset: controlItems,
        getId: (item: any) => item?.value,
        getLabel: (item: any) => item?.label || item?.value || "Control",
        getDetails: (item: any) => item?.description,
        linkValue: 5,
      });

      addCategory({
        id: "duck-clauses",
        label: "Clauses",
        group: "Governance",
        type: "Category",
        dataset: clauseItems,
        getId: (item: any) => item?.value,
        getLabel: (item: any) => item?.label || item?.value || "Clause",
        getDetails: (item: any) => item?.description,
        linkValue: 3,
      });

      // Risk category links to Scope
      addCategory({
        id: "duck-risks",
        label: "Risks",
        group: "Risk",
        type: "Category",
        dataset: risks,
        getId: (item: any) => item?.id || item?.riskID,
        getLabel: (item: any) => item?.description || item?.risk || "Risk",
        getDetails: (item: any) => `Rating: ${item?.riskRating || "Unknown"}`,
        linkValue: 3,
      });

      // Operational categories - will link to Controls
      addCategory({
        id: "duck-suppliers",
        label: "Suppliers",
        group: "Operations",
        type: "Category",
        dataset: suppliers,
        getId: (item: any) => item?.assetID || item?.id,
        getLabel: (item: any) => item?.assetName || item?.companyName || item?.supplierName || "Supplier",
        getDetails: (item: any) => item?.assetCat || item?.assetType,
      });

      addCategory({
        id: "duck-assets",
        label: "Assets",
        group: "Operations",
        type: "Category",
        dataset: plainAssets,
        getId: (item: any) => item?.assetID || item?.id,
        getLabel: (item: any) => item?.assetName || item?.asset || item?.componentId || "Asset",
        getDetails: (item: any) => item?.assetCat || item?.classification,
      });

      // Assurance - Audits link to Scope, Findings to Audits
      addCategory({
        id: "duck-audits",
        label: "Audits",
        group: "Assurance",
        type: "Category",
        dataset: audits,
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.name || item?.title || item?.scope || "Audit",
        getDetails: (item: any) => item?.type || item?.status,
        linkValue: 3,
      });

      addCategory({
        id: "duck-findings",
        label: "Findings",
        group: "Assurance",
        type: "Category",
        dataset: findings,
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.title || item?.summary || "Finding",
        getDetails: (item: any) => item?.status || item?.type,
      });

      // Documentation categories - will link to Controls
      addCategory({
        id: "duck-policies",
        label: "Policies",
        group: "Documentation",
        type: "Category",
        dataset: policies,
        getId: (item: any, index: number) => item?.id || item?.policyId || index,
        getLabel: (item: any) => item?.policy_name || item?.name || "Policy",
        getDetails: (item: any) => item?.status || item?.policy_type,
      });

      addCategory({
        id: "duck-documents",
        label: "Documents",
        group: "Documentation",
        type: "Category",
        dataset: documents,
        getId: (item: any, index: number) => item?.docID || item?.id || index,
        getLabel: (item: any) => item?.fileName || item?.name || item?.title || "Document",
        getDetails: (item: any) => item?.documentType || item?.status,
      });

      addCategory({
        id: "duck-evidence",
        label: "Evidence",
        group: "Documentation",
        type: "Category",
        dataset: evidenceItems,
        getId: (item: any, index: number) => item?.docID || item?.id || index,
        getLabel: (item: any) => item?.fileName || item?.name || "Evidence",
        getDetails: (item: any) => item?.classification || item?.status,
      });

      // Data categories - will link to Controls
      addCategory({
        id: "duck-data-types",
        label: "Data Types",
        group: "Data",
        type: "Category",
        dataset: dataTypes,
        getId: (item: any, index: number) => item?.dataTypeID || item?.id || index,
        getLabel: (item: any) => item?.dataType || item?.name || "Data Type",
        getDetails: (item: any) => item?.classification,
      });

      addCategory({
        id: "duck-storage",
        label: "Storage Locations",
        group: "Data",
        type: "Category",
        dataset: storageLocations.map((value, index) => ({ id: `storage-${index}`, value })),
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.value || "Storage Location",
      });

      // Privacy - will link to Controls
      addCategory({
        id: "duck-privacy",
        label: "Privacy Requests",
        group: "Privacy",
        type: "Category",
        dataset: privacyRequests,
        getId: (item: any, index: number) => item?.requestID || index,
        getLabel: (item: any) => item?.typeOfRequest || item?.status || "Privacy Request",
        getDetails: (item: any) => item?.status ? `Status: ${item.status}` : undefined,
      });

      // Operations - will link to Controls
      addCategory({
        id: "duck-tasks",
        label: "Assurance Tasks",
        group: "Operations",
        type: "Category",
        dataset: tasks,
        getId: (item: any, index: number) => item?.assID || index,
        getLabel: (item: any) => item?.checkDetail?.check || "Assurance Task",
        getDetails: (item: any) => item?.checkDetail?.assignedTo ? `Owner: ${item.checkDetail.assignedTo}` : undefined,
      });

      addCategory({
        id: "duck-checklists",
        label: "Checklists",
        group: "Operations",
        type: "Category",
        dataset: checklists,
        getId: (item: any) => item?.id,
        getLabel: (item: any) => item?.title || "Checklist",
        getDetails: (item: any) => `Status: ${item?.status || "Unknown"}`,
      });

      // Create hub-and-spoke topology
      // Controls is the central hub - link operational/assurance categories to it
      const controlsHubId = "duck-controls";
      
      const categoriesToLinkToControls = [
        "duck-risks",
        "duck-assets",
        "duck-suppliers",
        "duck-policies",
        "duck-documents",
        "duck-evidence",
        "duck-data-types",
        "duck-storage",
        "duck-privacy",
        "duck-tasks",
        "duck-checklists",
      ];

      categoriesToLinkToControls.forEach(categoryId => {
        links.push({ source: controlsHubId, target: categoryId, value: 3 });
      });

      // Findings link to Audits (not Controls)
      links.push({ source: "duck-audits", target: "duck-findings", value: 3 });

      setGraphData({ nodes, links });
      setMetrics(summaryMetrics);
    } catch (err) {
      console.error("[DUCK] Failed to load graph data", err);
      setError(err instanceof Error ? err.message : "Failed to load DUCK data");
      setGraphData({ nodes: [], links: [] });
      setMetrics({});
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, activeTenancy?.name, isModuleEnabled]);

  useEffect(() => {
    fetchDuckData();
  }, [fetchDuckData]);

  useEffect(() => {
    if (!graphContainerRef.current || forceGraphRef.current) {
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const ForceGraph3D = (await import("3d-force-graph")).default;
        if (!isMounted || !graphContainerRef.current) return;

        const containerWidth = graphContainerRef.current.offsetWidth || window.innerWidth - 64;
        const containerHeight = graphContainerRef.current.offsetHeight || 700;
        
        const Graph = ForceGraph3D();
        const instance = Graph(graphContainerRef.current);
        instance
          .width(containerWidth)
          .height(containerHeight)
          .showNavInfo(false)
          .nodeRelSize(6)
          .nodeOpacity(0.9)
          .linkOpacity(0.8)
          .linkWidth(2)
          .linkDirectionalParticles(4)
          .linkDirectionalParticleWidth(2)
          .nodeAutoColorBy("group")
          .nodeVal((node: GraphNode) => node.size ?? 4)
          .nodeLabel((node: GraphNode) => {
            const detail = node.details ? `<br/><span style="font-size: 11px; opacity: 0.75;">${node.details}</span>` : "";
            return `<div style="max-width: 240px"><strong>${node.label}</strong><br/><span style="font-size: 12px;">${node.type}</span>${detail}</div>`;
          });

        instance.d3Force("charge")?.strength(-140);

        forceGraphRef.current = instance;
        instance.graphData(graphData);

        const background = isDark ? "#020617" : "#f8fafc";
        instance.backgroundColor(background);
        instance.linkColor(() => (isDark ? "rgba(96,165,250,0.9)" : "rgba(37,99,235,0.75)"));

        // Add bloom effect for that sweet glow
        const { UnrealBloomPass } = await import("three/examples/jsm/postprocessing/UnrealBloomPass.js");
        const bloomPass = new UnrealBloomPass();
        bloomPass.strength = isDark ? 1.5 : 0.8;
        bloomPass.radius = 1;
        bloomPass.threshold = 0.1;
        instance.postProcessingComposer().addPass(bloomPass);
      } catch (err) {
        console.error("[DUCK] Failed to initialise 3D graph", err);
        setError("Failed to initialise graph renderer");
      }
    })();

    return () => {
      isMounted = false;
      if (forceGraphRef.current) {
        try {
          forceGraphRef.current._destructor?.();
        } catch (err) {
          // noop
        }
        forceGraphRef.current = null;
      }
      if (graphContainerRef.current) {
        graphContainerRef.current.innerHTML = "";
      }
    };
  }, [graphData, isDark]);

  useEffect(() => {
    if (forceGraphRef.current) {
      forceGraphRef.current.graphData(graphData);
    }
  }, [graphData]);

  useEffect(() => {
    if (forceGraphRef.current) {
      const background = isDark ? "#020617" : "#f8fafc";
      forceGraphRef.current.backgroundColor(background);
      forceGraphRef.current.linkColor(() => (isDark ? "rgba(96,165,250,0.9)" : "rgba(37,99,235,0.75)"));
      
      // Update bloom strength for theme
      try {
        const composer = forceGraphRef.current.postProcessingComposer();
        const passes = composer.passes;
        const bloomPass = passes.find((p: any) => p.constructor.name === "UnrealBloomPass");
        if (bloomPass) {
          bloomPass.strength = isDark ? 1.5 : 0.8;
        }
      } catch (err) {
        // Bloom pass might not be initialized yet
      }
    }
  }, [isDark]);

  const summaryItems = useMemo(
    () => [
      { key: "obligations", label: "Legal Obligations" },
      { key: "parties", label: "Interested Parties" },
      { key: "needs", label: "Needs & Expectations" },
      { key: "objectives", label: "Objectives" },
      { key: "clauses", label: "Clauses" },
      { key: "controls", label: "Controls" },
      { key: "risks", label: "Risks" },
      { key: "suppliers", label: "Suppliers" },
      { key: "assets", label: "Assets" },
      { key: "findings", label: "Findings" },
      { key: "audits", label: "Audits" },
      { key: "policies", label: "Policies" },
      { key: "documents", label: "Documents" },
      { key: "dataTypes", label: "Data Types" },
      { key: "storageLocations", label: "Storage Locations" },
      { key: "privacyRequests", label: "Privacy Requests" },
      { key: "tasks", label: "Assurance Tasks" },
      { key: "checklists", label: "Checklists" },
      { key: "evidence", label: "Evidence" },
    ],
    []
  );

  return (
    <div className="w-full space-y-6">
      <Card className="mx-4">
        <CardHeader>
          <CardTitle>Dynamic Understanding of Compliance Knowledge</CardTitle>
          <CardDescription>
            A live synthesis of scope, obligations, risks, governance artefacts, and operational evidence for{" "}
            {activeTenancy?.name || "your tenancy"}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {summaryItems.map((item) => (
              <div
                key={item.key}
                className="rounded-lg border border-dashed border-primary/20 bg-muted/30 p-4 backdrop-blur-sm"
              >
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{item.label}</p>
                <p className="text-2xl font-semibold text-foreground">
                  {metrics[item.key] !== undefined ? metrics[item.key] : "–"}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card ref={graphCardRef} className="relative min-h-[720px] mx-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Knowledge Graph</CardTitle>
              <CardDescription>Hover to inspect relationships; drag or scroll to explore the 3D space.</CardDescription>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              className="shrink-0"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent className={`relative p-0 ${isFullscreen ? 'h-[calc(100vh-8rem)]' : 'h-[700px]'}`}>
          <div ref={graphContainerRef} className="h-full w-full max-w-full overflow-hidden" style={{ maxWidth: '100%' }} />
          {(loading || !graphData.nodes.length) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-sm">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Preparing DUCK data…</span>
            </div>
          )}
          {error && (
            <div className="absolute inset-x-0 bottom-6 mx-auto w-fit rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Duck;
